import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BottledWinesToolbar from '../components/toolbars/BottledWinesToolbar';
import '../index.css';

const BottledWinesList = () => {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [brands, setBrands] = useState([]);
    const [fancifulNames, setFancifulNames] = useState([]);
    const [vineyards, setVineyards] = useState([]);
    const [modalProduct, setModalProduct] = useState({
        proVintage: '',
        proBrand: '',
        proFanciful: '',
        proVarietal: '',
        proVineyard: '',
        proWineType: '',
        proBottledDate: '',
        proBottledQuantity: '',
        proLiters: '',
        proPack: '',
        proWeight: '',
        proAlc: '',
        proVersion: '',
        proAppellation: ''
    });
    const [vintages, setVintages] = useState([]);
    const [varietals, setVarietals] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const generateVintages = () => {
            const currentYear = new Date().getFullYear();
            const years = ['0'];
            for (let i = currentYear; i >= currentYear - 12; i--) {
                years.push(i.toString());
            }
            setVintages(years);
        };

        const fetchInitialData = async () => {
            try {
                const [
                    productsResponse, 
                    varietalsResponse, 
                    vineyardsResponse,
                    brandsResponse, 
                    fancifulResponse
                ] = await Promise.all([
                    axios.get('/api/products', {
                        params: { page, limit: 20, search }
                    }),
                    axios.get('/api/varietals'),
                    axios.get('/api/vineyards'),
                    axios.get('/api/brands'),
                    axios.get('/api/fanciful-names')
                ]);

                setProducts(productsResponse.data.products);
                setTotalPages(productsResponse.data.totalPages);
                setVarietals(varietalsResponse.data);
                setVineyards(vineyardsResponse.data);
                setBrands(brandsResponse.data);
                setFancifulNames(fancifulResponse.data);
            } catch (error) {
                console.error('Error fetching initial data:', error);
            }
        };

        generateVintages();
        fetchInitialData();
    }, [page, search]);

    const handleRowClick = (product) => {
        // Format the bottled date properly for the form
        const formattedDate = product.proBottledDate 
            ? new Date(product.proBottledDate).toISOString().split('T')[0] 
            : '';
            
        // Create a complete modal product object with all needed fields
        setModalProduct({
            ...product,
            proBottledDate: formattedDate,
            // Ensure these fields are included even if they're null in the response
            proVersion: product.proVersion || '',
            proAppellation: product.proAppellation || ''
        });
        
        setIsEditing(true);
        setShowForm(true);
    };

    const handleAddNew = () => {
        setModalProduct({
            proVintage: '',
            proBrand: '',
            proFanciful: '',
            proVarietal: '',
            proVineyard: '',
            proWineType: '',
            proBottledDate: '',
            proBottledQuantity: '',
            proLiters: '',
            proPack: '',
            proWeight: '',
            proAlc: '',
            proVersion: ''
        });
        setIsEditing(false);
        setShowForm(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const productData = {
                ...modalProduct,
                proBottledDate: modalProduct.proBottledDate 
                    ? new Date(modalProduct.proBottledDate).toISOString().split('T')[0] 
                    : null,
                proBottledQuantity: parseInt(modalProduct.proBottledQuantity) || 0,
                proLiters: parseFloat(modalProduct.proLiters) || 0,
                proPack: parseInt(modalProduct.proPack) || 0,
                proWeight: parseFloat(modalProduct.proWeight) || 0,
                proAlc: parseFloat(modalProduct.proAlc) || 0
            };

            if (isEditing) {
                await axios.put(`/api/products/${modalProduct.productID}`, productData);
            } else {
                await axios.post('/api/products', productData);
            }
            
            setShowForm(false);
            const response = await axios.get('/api/products', {
                params: { page, limit: 20, search }
            });
            setProducts(response.data.products);
        } catch (error) {
            console.error('Error saving product:', error);
            setError(error.response?.data?.message || 'Error saving product');
        } finally {
            setSaving(false);
        }
    };

    const handleArchiveProduct = async (productID, e) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to archive this product?")) return;

        try {
            await axios.put(`/api/products/${productID}/archive`);
            const response = await axios.get('/api/products', {
                params: { page, limit: 50, search }
            });
            setProducts(response.data.products);
        } catch (error) {
            console.error('Error archiving product:', error);
        }
    };

    return (
        <>
            <BottledWinesToolbar 
                onAdd={handleAddNew}
                search={search}
                onSearchChange={setSearch}
            />
            
            <div className="container mt-4">
                {showForm && (
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5 className="mb-0">
                                {isEditing ? 'Edit Bottled Wine' : 'Add New Bottled Wine'}
                            </h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSave}>
                                {error && (
                                    <div className="alert alert-danger">
                                        {error}
                                    </div>
                                )}
                                
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Vintage</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proVintage}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVintage: e.target.value
                                            })}
                                        >
                                            <option value="0">Non-Vintage</option>
                                            {vintages.filter(v => v !== '0').map((vintage, index) => (
                                                <option key={index} value={vintage}>
                                                    {vintage}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Brand</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proBrand}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proBrand: e.target.value
                                            })}
                                            required
                                        >
                                            <option value="">Select Brand</option>
                                            {brands.map((brand) => (
                                                <option key={brand.id} value={brand.name}>
                                                    {brand.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                            <label className="form-label">Appellation</label>
                                            <select
                                                className="form-control"
                                                value={modalProduct.proAppellation || ''}
                                                onChange={(e) => setModalProduct({
                                                    ...modalProduct,
                                                    proAppellation: e.target.value
                                                })}
                                            >
                                                <option value="">Select Appellation</option>
                                                <option value="Lodi">Lodi</option>
                                                <option value="Amador">Amador</option>
                                                <option value="California">California</option>
                                            </select>
                                        </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-5 mb-3">
                                        <label className="form-label">Fanciful Name</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proFanciful}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proFanciful: e.target.value
                                            })}
                                        >
                                            <option value="">Select Fanciful Name</option>
                                            {fancifulNames.map((name) => (
                                                <option key={name.id} value={name.name}>
                                                    {name.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-5 mb-3">
                                        <label className="form-label">Varietal</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proVarietal}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVarietal: e.target.value
                                            })}
                                            required
                                        >
                                            <option value="">Select Varietal</option>
                                            {varietals.map((varietal, index) => (
                                                <option key={index} value={varietal}>
                                                    {varietal}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2 mb-3">
                                            <label className="form-label">Version</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={modalProduct.proVersion}
                                                onChange={(e) => setModalProduct({
                                                    ...modalProduct,
                                                    proVersion: e.target.value
                                                })}
                                                placeholder="e.g., v1, 45"
                                            />
                                        </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Vineyard</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proVineyard}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proVineyard: e.target.value
                                            })}
                                        >
                                            <option value="">Select Vineyard</option>
                                            {vineyards.map((vineyard) => (
                                                <option key={vineyard.id} value={vineyard.vineyardName}>
                                                    {vineyard.vineyardName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Wine Type</label>
                                        <select
                                            className="form-control"
                                            value={modalProduct.proWineType}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proWineType: e.target.value
                                            })}
                                        >
                                            <option value="">Select Wine Style</option>
                                            <option value="Red">Red</option>
                                            <option value="White">White</option>
                                            <option value="Rose">Rosé</option>
                                            <option value="Sweet">Sweet</option>
                                            <option value="Late Harvest">Dessert</option>
                                            <option value="Piquette">Piquette</option>
                                            <option value="Pet-Nat">Pet-Nat</option>
                                            <option value="Port">Port</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Bottled Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={modalProduct.proBottledDate}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proBottledDate: e.target.value
                                            })}
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Bottled Quantity</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="form-control"
                                            value={modalProduct.proBottledQuantity}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proBottledQuantity: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Alcohol Content (%)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            className="form-control"
                                            value={modalProduct.proAlc}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proAlc: Math.max(0, Math.min(100, Number(e.target.value)))
                                            })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Liters</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.001"
                                            className="form-control"
                                            value={modalProduct.proLiters}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proLiters: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Pack</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="form-control"
                                            value={modalProduct.proPack}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proPack: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Weight</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={modalProduct.proWeight}
                                            onChange={(e) => setModalProduct({
                                                ...modalProduct,
                                                proWeight: Math.max(0, Number(e.target.value))
                                            })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            setShowForm(false);
                                            setIsEditing(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-outline-primary"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : isEditing ? 'Update Bottled Wine' : 'Add Bottled Wine'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Vintage</th>
                                        <th>Brand</th>
                                        <th>Fanciful</th>
                                        <th>Appellation</th>
                                        <th>Version</th>
                                        <th>Varietal</th>
                                        <th>Vineyard</th>
                                        <th>Quantity</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr
                                            key={product.productID}
                                            onClick={() => handleRowClick(product)}
                                            style={{ cursor: 'pointer' }}
                                            className="table-row-hover"
                                        >
                                            <td>{product.productID}</td>
                                            <td>{product.proVintage === 0 ? 'Non-Vintage' : product.proVintage}</td>
                                            <td>{product.proBrand}</td>
                                            <td>{product.proFanciful}</td>
                                            <td>{product.proAppellation || '-'}</td>
                                            <td>{product.proVersion || '-'}</td>
                                            <td>{product.proVarietal}</td>
                                            <td>{product.proVineyard}</td>
                                            <td>{product.proBottledQuantity}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={(e) => handleArchiveProduct(product.productID, e)}
                                                >
                                                    Archive
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    <div className="card-footer">
                        <nav aria-label="Page navigation">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${page === index + 1 ? 'active' : ''}`}
                                    >
                                        <button 
                                            className="page-link" 
                                            onClick={() => setPage(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setPage(page + 1)}
                                        disabled={page === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>

        </>
    );
};

export default BottledWinesList;